import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import type { AuthUser } from '@attendance/shared';
import { getDeviceId } from './deviceFingerprint.js';

let accessToken: string | null = null;
// Holds the in-flight session rotation so EVERY caller shares ONE
// /auth/refresh call (see rotateSession below). null when none is running.
let refreshPromise: Promise<{ user: AuthUser; accessToken: string }> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * "This browser asked to sign out and the server never confirmed it."
 *
 * The refresh cookie is httpOnly, so a client whose logout request failed
 * cannot clear it — and the cookie alone is enough for `bootstrap()` to mint a
 * fresh access token on the next page load. Without this marker, a sign-out
 * that failed on a dropped connection hands the session to the next person to
 * open the browser, silently, on a machine that may well be shared: a site
 * office desktop, a camp supervisor's tablet.
 *
 * Kept in localStorage because it has to outlive the page. Reads and writes are
 * guarded: Safari private mode throws on access, and a storage failure must not
 * be the thing that stops someone signing out.
 */
const PENDING_LOGOUT_KEY = 'hadir.pendingLogout';

export function markLogoutPending(): void {
  try {
    window.localStorage.setItem(PENDING_LOGOUT_KEY, '1');
  } catch {
    // No storage available. The in-memory token is still cleared by the caller,
    // so this degrades to the pre-existing behaviour rather than breaking.
  }
}

export function clearPendingLogout(): void {
  try {
    window.localStorage.removeItem(PENDING_LOGOUT_KEY);
  } catch {
    /* ignore */
  }
}

export function isLogoutPending(): boolean {
  try {
    return window.localStorage.getItem(PENDING_LOGOUT_KEY) === '1';
  } catch {
    return false;
  }
}

export interface ApiErrorPayload {
  error: { code: string; message: string; details?: unknown };
}

/**
 * On Vercel (or any host that's not co-located with the API), set
 * VITE_API_URL at build time, e.g. https://your-api.onrender.com.
 * Locally, vite.config.ts proxies /api → http://localhost:3001 so the default
 * '/api' works.
 */
export const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '') + '/api';

// A finite request timeout so a request the server never answers rejects
// instead of hanging forever. Without it, a stalled write (e.g. a slow or
// wedged upstream on a shared host) leaves the UI pinned on "Saving…" with no
// error ever surfaced — the mutation's promise simply never settles. 30s is
// well above any normal CRUD round-trip; large report exports opt into a longer
// window via `download` below.
const DEFAULT_TIMEOUT_MS = 30_000;

const client: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  // Stable per-browser id — bound to the session at login and stamped on
  // punches so the server can spot one device used by multiple workers.
  config.headers['x-device-id'] = getDeviceId();
  return config;
});

/**
 * Single-flight access-token refresh. The access token lives ~15 minutes; a
 * data-heavy page typically fires several requests that all 401 together when
 * it expires. The server rotates refresh tokens with RFC 6749 §10.4 reuse
 * detection, so if each 401 POSTed its own /auth/refresh, the second would
 * replay the just-rotated token, the server would flag reuse and revoke the
 * whole token family, and the user would be kicked to /login mid-session.
 * Sharing one in-flight promise guarantees exactly one /auth/refresh per
 * expiry — every concurrent 401 awaits the same rotation, then retries.
 */
async function rotateSession(): Promise<{ user: AuthUser; accessToken: string }> {
  if (!refreshPromise) {
    refreshPromise = client
      .post<{ data: { user: AuthUser; accessToken: string } }>('/auth/refresh')
      .then((res) => {
        setAccessToken(res.data.data.accessToken);
        return res.data.data;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function refreshAccessToken(): Promise<string> {
  return (await rotateSession()).accessToken;
}

client.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (axios.isAxiosError<ApiErrorPayload>(err) && err.response?.status === 401) {
      const original = err.config;
      const isAuthRoute = original?.url?.startsWith('/auth/');
      if (original && !isAuthRoute && !(original as { _retry?: boolean })._retry) {
        (original as { _retry?: boolean })._retry = true;
        try {
          const token = await refreshAccessToken();
          if (original.headers) {
            original.headers.Authorization = `Bearer ${token}`;
          }
          return client(original);
        } catch {
          setAccessToken(null);
          window.location.assign('/login');
        }
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  async login(
    email: string,
    password: string,
    totpToken?: string
  ): Promise<{ user: AuthUser; accessToken: string }> {
    const { data } = await client.post<{ data: { user: AuthUser; accessToken: string } }>(
      '/auth/login',
      { email, password, ...(totpToken ? { totpToken } : {}) }
    );
    setAccessToken(data.data.accessToken);
    return data.data;
  },

  /**
   * SESSION RESTORE, THROUGH THE SAME SINGLE FLIGHT AS THE 401 RETRY.
   *
   * This used to POST /auth/refresh directly, outside `refreshPromise`, and
   * that was the whole bug: the reuse detection described above does not care
   * WHICH caller replayed a rotated token. React StrictMode double-invokes
   * effects in development, so every hard page load ran `bootstrap()` twice and
   * fired two rotations; the second replayed the token the first had just
   * spent, the server revoked the family, and the reader was thrown to /login
   * from a page they had every right to open. In production the same shape
   * arrives whenever two tabs restore at once, or a bootstrap overlaps a 401
   * retry — rarer, and identical in effect.
   *
   * Sharing the flight makes a concurrent restore free: the second caller
   * awaits the first rotation and gets the same user back.
   */
  async refresh(): Promise<{ user: AuthUser; accessToken: string }> {
    return rotateSession();
  },

  /**
   * SIGNING OUT MUST NOT DEPEND ON THE NETWORK.
   *
   * This used to `await` the server call and only then drop the token, so a
   * request that failed left the access token in memory and the caller's state
   * untouched — the person had clicked Sign out and stayed signed in. On a site
   * with bad signal, which is where this product runs, that is not a rare path.
   *
   * The local half is now unconditional. The server half is best-effort and
   * reported rather than thrown, because the caller cannot do anything useful
   * with an exception here but very much needs to know the refresh cookie may
   * still be live: it is httpOnly, so only the server can clear it, and until
   * it is cleared `bootstrap()` would hand the session to whoever opens the
   * browser next. `markLogoutPending` is what closes that gap.
   */
  async logout(): Promise<{ revokedOnServer: boolean }> {
    try {
      await client.post('/auth/logout');
      clearPendingLogout();
      return { revokedOnServer: true };
    } catch {
      markLogoutPending();
      return { revokedOnServer: false };
    } finally {
      setAccessToken(null);
    }
  },

  /** Best-effort retry of a sign-out the server never confirmed. */
  async retryPendingLogout(): Promise<boolean> {
    try {
      await client.post('/auth/logout');
      clearPendingLogout();
      return true;
    } catch {
      return false;
    }
  },

  async me(): Promise<AuthUser> {
    const { data } = await client.get<{ data: { user: AuthUser } }>('/auth/me');
    return data.data.user;
  },

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const { data } = await client.get<T>(url, config);
    return data;
  },
  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const { data } = await client.post<T>(url, body, config);
    return data;
  },
  /* PUT, not PATCH: the one caller today (`/erp/connection`) replaces a whole
     settings block, and a partial write against a block whose fields are lists
     would silently keep a stale payroll nobody meant to leave behind. */
  async put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const { data } = await client.put<T>(url, body, config);
    return data;
  },
  async patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const { data } = await client.patch<T>(url, body, config);
    return data;
  },
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const { data } = await client.delete<T>(url, config);
    return data;
  },

  /**
   * Issues an authenticated GET that returns the full Axios response so
   * callers can inspect headers (e.g. `Content-Disposition`) and the raw
   * body (Blob / ArrayBuffer / etc.). Used by `lib/download.ts` for
   * authenticated binary downloads — `<a href>` would not send the
   * in-memory bearer token.
   */
  /**
   * The same authenticated binary fetch, as a POST with a JSON body.
   *
   * One endpoint needs it: the report builder's Excel export, which sends the
   * table it has already computed and asks the server to lay it out (see
   * `reportBuilderExport.schema.ts`). A GET cannot carry that — the rows are
   * the request — and encoding a several-thousand-row table into a query
   * string would fail at the proxy long before it failed at the server.
   *
   * Everything else about it is `download` above: the same generous ceiling,
   * the same blob-error revival, so a 4xx still arrives with the server's own
   * sentence rather than "Request failed with status code 422".
   */
  async downloadPost(
    url: string,
    body: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Blob>> {
    try {
      return await client.post<Blob>(url, body, {
        responseType: 'blob',
        timeout: 120_000,
        ...(config ?? {}),
      });
    } catch (err) {
      await reviveBlobErrorBody(err);
      throw err;
    }
  },

  async download(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Blob>> {
    // Report exports can take longer than a CRUD call to generate on a slow
    // host, so give downloads a more generous ceiling than the global default
    // (still finite, so a wedged export doesn't hang indefinitely). Callers may
    // override via `config.timeout`.
    try {
      return await client.get<Blob>(url, {
        responseType: 'blob',
        timeout: 120_000,
        ...(config ?? {}),
      });
    } catch (err) {
      // `responseType: 'blob'` applies to the ERROR body too, so a 4xx arrives
      // with `response.data` as a Blob and the structured `{ error: {...} }`
      // payload unreadable — `getApiErrorContext` fell through to axios's own
      // "Request failed with status code 422" for EVERY failed export. The
      // server's actionable message ("No WPS establishment ID configured on
      // Acme Dubai — set it on the branch") never reached the operator.
      // Re-parse the blob into the shape the rest of the app already expects,
      // so every downstream reader keeps working unchanged.
      await reviveBlobErrorBody(err);
      throw err;
    }
  },
};

/**
 * If `err` is an Axios error whose body is a Blob of JSON, replace it in place
 * with the parsed object. Silent no-op for a genuinely binary or malformed
 * body — a download that fails for a non-API reason must not become a parse
 * error.
 */
async function reviveBlobErrorBody(err: unknown): Promise<void> {
  if (!axios.isAxiosError(err)) return;
  const data: unknown = err.response?.data;
  if (!(data instanceof Blob)) return;
  try {
    const text = await data.text();
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      (err.response as { data: unknown }).data = parsed;
    }
  } catch {
    // Not JSON — leave the Blob alone and let the generic message stand.
  }
}

export interface ApiErrorContext {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  status?: number;
}

export interface AuthErrorContext {
  code: string;
  message: string;
  details?: { unlockAt?: string } & Record<string, unknown>;
  status?: number;
}

/**
 * Generic API error helper used by feature pages (punch, etc.) — branch on
 * `code` instead of pattern-matching messages. For user-facing text, prefer
 * `friendlyMessage` from `lib/errors.ts`, which maps codes to plain English
 * and handles offline/transport failures.
 */
export function getApiErrorContext(err: unknown): ApiErrorContext {
  if (axios.isAxiosError<ApiErrorPayload>(err)) {
    const payload = err.response?.data?.error;
    return {
      code: payload?.code ?? 'UNKNOWN',
      message: payload?.message ?? err.message,
      details: payload?.details as Record<string, unknown> | undefined,
      status: err.response?.status,
    };
  }
  if (err instanceof Error) {
    return { code: 'UNKNOWN', message: err.message };
  }
  return { code: 'UNKNOWN', message: 'An unexpected error occurred' };
}

/**
 * Pulls the structured `error.code` out of an API error so the auth pages
 * can branch on it (locked vs rate-limited vs suspended vs wrong-creds).
 * Same extraction as `getApiErrorContext`, with typed `unlockAt` details.
 */
export function getAuthErrorContext(err: unknown): AuthErrorContext {
  const ctx = getApiErrorContext(err);
  return { ...ctx, details: ctx.details as AuthErrorContext['details'] };
}
